import React, { useRef } from 'react';
import {
  FormInstance, Upload, Button, UploadFile, Alert, App, Form, Avatar,
} from 'antd';
import clsx from 'clsx';
import { DeleteOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons';
import { RcFile } from 'antd/es/upload';
import ImgCrop, { ImgCropProps } from 'antd-img-crop';
import { getMessageInError } from '../../../hooks/fetch';
import { getBase64 } from '../../../utils';
import { useUploadFile } from '../../../hooks/file';
import { useMessageError } from '../../../hooks/common';
import { IconTrash, IconUpdate } from '../Icon';

import styles from './index.module.scss';

export const getFullFileUrl = (url: string | undefined) => (url ? `${process.env.REACT_APP_AMAZON_S3_URL}/${url}` : '');

interface InputCodeWrapperProps {
  name: string;
  fileList: UploadFile[];
  setFileList: React.Dispatch<React.SetStateAction<UploadFile[]>>;
  required?: boolean;
  form?: FormInstance;
  accept?: string;
  uploadUrl?: string;
  className?: string;
  cropperProps?: Partial<ImgCropProps>;
  onUploadSuccess?: (fileId: string) => void;
}

function ImageUpload({
  form,
  name,
  fileList,
  setFileList,
  required = false,
  accept = '.jpg,.png,.jpeg',
  uploadUrl = 'files/upload',
  className,
  cropperProps,
  onUploadSuccess,
}: InputCodeWrapperProps) {
  const { message } = App.useApp();
  const uploader = useUploadFile(uploadUrl);
  const uploadRef = useRef(null);

  useMessageError([uploader]);

  const cropperParams: Partial<ImgCropProps> = {
    rotationSlider: true,
    showReset: true,
    resetText: 'Reset',
    modalTitle: 'Edit photo',
    ...cropperProps,
  };

  const beforeUpload = async (file: RcFile) => {
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      message.error('You can only upload JPG/PNG file!');

      return false;
    }
    if (file.size / 1024 / 1024 > 5) {
      message.error('Image must be smaller than 5MB!');

      return false;
    }

    const newFile: UploadFile = {
      originFileObj: file,
      uid: file.uid,
      name: file.name,
      status: 'done',
    };

    newFile.preview = await getBase64(file) as string;

    setFileList([newFile]);

    const formData = new FormData();

    formData.append('file', newFile.originFileObj as Blob);
    uploader.fetch(formData).then((res) => {
      if (res?.url) {
        setFileList([{ ...newFile, url: res.url }]);
      }
      if (res?.id) {
        onUploadSuccess?.(res.id);
      }
    });

    return false;
  };

  const handleRemove = () => {
    setFileList([]);
  };

  return (
    <div className={clsx(styles.uploader, className)}>
      <Avatar
        size={105}
        src={fileList[0]?.preview || fileList[0]?.url || '/media/userIcon.png'}
        className={styles.avatar}
      />
      <div style={{ marginLeft: '10px', display: 'flex', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '5px', marginBottom: '8px' }}>
          <Form.Item
            name={name}
            rules={required ? [{ required: true, message: 'This field is required' }] : undefined}
            className="no-space-form-item"
          >
            {fileList.length > 0 && (
              <Button type="link" icon={<IconTrash />} onClick={handleRemove}>Delete</Button>
            )}
            <ImgCrop {...cropperParams}>
              <Upload
                showUploadList={false}
                accept={accept}
                beforeUpload={beforeUpload}
              >
                <Button type="link" icon={<IconUpdate />}>Update</Button>
              </Upload>
            </ImgCrop>
          </Form.Item>

        </div>
      </div>
      {uploader.error && (
        <Alert
          style={{ marginTop: 8 }}
          type="error"
          message={getMessageInError(uploader.error)}
          closable
          onClose={uploader.clearError}
        />
      )}
    </div>
  );
}

export default ImageUpload;
